import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiAward, FiUsers, FiTrendingUp, FiDollarSign, FiPlus, FiArrowRight } from 'react-icons/fi';
import EventService from '../services/EventService';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalEvents: 0,
    totalContests: 0,
    totalAttendees: 0,
    totalRevenue: 0,
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const statsRes = await EventService.getDashboardStats().catch(() => null);
        const eventsRes = await EventService.getAllPartnerEvents().catch(() => null);

        const evList = eventsRes?.events || eventsRes?.data || eventsRes || [];
        const s = statsRes?.summary || statsRes?.stats || statsRes || {};

        setStats({
          totalEvents: s.totalEvents ?? (Array.isArray(evList) ? evList.length : 0),
          totalContests: s.totalContests ?? 0,
          totalAttendees: s.totalAttendees ?? s.totalAudience ?? 0,
          totalRevenue: s.totalRevenue ?? 0,
        });
      } catch (err) {
        console.error('Error fetching partner dashboard stats:', err);
      }
    };
    fetchDashboardData();
  }, []);

  return (
    <div className="space-y-6 text-left pb-16">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Partner Overview Dashboard</h1>
          <p className="text-xs font-medium text-slate-500 mt-1">Manage events, contests, ticket sales & check-ins in real-time</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/app/contests/create"
            className="px-4 py-2.5 rounded-xl bg-purple-50 hover:bg-purple-100 text-purple-900 text-xs font-bold transition-all flex items-center gap-2 border border-purple-200 shadow-sm"
          >
            <FiAward className="w-4 h-4 text-purple-700" /> + Create Contest
          </Link>
          <Link
            to="/app/create"
            className="px-4 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold transition-all shadow-sm flex items-center gap-2"
          >
            <FiPlus className="w-4 h-4" /> + Create IRL Event
          </Link>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 p-5 rounded-2xl space-y-2 relative overflow-hidden shadow-sm">
          <div className="flex items-center justify-between text-purple-700">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-600">Total Events</span>
            <FiCalendar className="w-5 h-5" />
          </div>
          <h3 className="text-3xl font-black text-slate-900">{stats.totalEvents}</h3>
          <span className="text-[11px] text-emerald-700 font-extrabold flex items-center gap-1">
            <FiTrendingUp className="w-3.5 h-3.5" /> Active & Upcoming
          </span>
        </div>

        <div className="bg-white border border-slate-200 p-5 rounded-2xl space-y-2 relative overflow-hidden shadow-sm">
          <div className="flex items-center justify-between text-indigo-700">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-600">Total Contests</span>
            <FiAward className="w-5 h-5" />
          </div>
          <h3 className="text-3xl font-black text-slate-900">{stats.totalContests}</h3>
          <span className="text-[11px] text-purple-700 font-extrabold flex items-center gap-1">
            <FiTrendingUp className="w-3.5 h-3.5" /> Talent Challenges
          </span>
        </div>

        <div className="bg-white border border-slate-200 p-5 rounded-2xl space-y-2 relative overflow-hidden shadow-sm">
          <div className="flex items-center justify-between text-emerald-700">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-600">Total Attendees</span>
            <FiUsers className="w-5 h-5" />
          </div>
          <h3 className="text-3xl font-black text-slate-900">{stats.totalAttendees}</h3>
          <span className="text-[11px] text-slate-500 font-medium">Registered across events</span>
        </div>

        <div className="bg-white border border-slate-200 p-5 rounded-2xl space-y-2 relative overflow-hidden shadow-sm">
          <div className="flex items-center justify-between text-amber-600">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-600">Ticket Revenue</span>
            <FiDollarSign className="w-5 h-5" />
          </div>
          <h3 className="text-3xl font-black text-slate-900">₹{stats.totalRevenue.toLocaleString()}</h3>
          <span className="text-[11px] text-emerald-700 font-extrabold">Realtime API</span>
        </div>
      </div>

      {/* Main Features Showcase Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Events Card */}
        <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-purple-900 font-extrabold text-sm">
              <FiCalendar className="w-5 h-5 text-purple-700" />
              <h2>Events Management (IRL, Online, Hybrid)</h2>
            </div>
            <Link to="/app/events" className="text-xs text-purple-700 hover:text-purple-800 hover:underline flex items-center gap-1 font-bold">
              View All <FiArrowRight />
            </Link>
          </div>
          <p className="text-xs text-slate-600 font-medium">Create physical/offline events, assign venue location & map link, set multi-tier tickets, and track registrations.</p>
          <div className="flex gap-2">
            <Link to="/app/create" className="px-4 py-2 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition-colors shadow-sm">
              + Create Event
            </Link>
            <Link to="/app/events" className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 border border-slate-200 text-xs font-bold hover:bg-slate-200 transition-colors shadow-sm">
              Manage Events List
            </Link>
          </div>
        </div>

        {/* Contests Card */}
        <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-indigo-900 font-extrabold text-sm">
              <FiAward className="w-5 h-5 text-indigo-700" />
              <h2>Contests & Challenges</h2>
            </div>
            <Link to="/app/contests" className="text-xs text-indigo-700 hover:text-indigo-800 hover:underline flex items-center gap-1 font-bold">
              View All <FiArrowRight />
            </Link>
          </div>
          <p className="text-xs text-slate-600 font-medium">Host photo/video competitions, attach contests to physical events, manage voting rules and track participant entries.</p>
          <div className="flex gap-2">
            <Link to="/app/contests/create" className="px-4 py-2 rounded-xl bg-indigo-700 text-white text-xs font-bold hover:bg-indigo-800 transition-colors shadow-sm">
              + Create Contest
            </Link>
            <Link to="/app/contests" className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 border border-slate-200 text-xs font-bold hover:bg-slate-200 transition-colors shadow-sm">
              Manage Contests List
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
